import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ChevronLeft, ChevronRight, Plus, Loader2, Play } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ScheduleShiftModal from "@/components/ScheduleShiftModal";
import ShiftDetailModal from "@/components/ShiftDetailModal";

// Monday-anchored week
function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - day);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function isoDate(d) {
  const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function Roster() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [workers, setWorkers] = useState([]);
  const [clients, setClients] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // { mode, prefill?, shift? }
  const [detailShift, setDetailShift] = useState(null);
  const [workerFilter, setWorkerFilter] = useState("");
  const [busyClockIn, setBusyClockIn] = useState(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = isoDate(weekStart);
      const to = isoDate(addDays(weekStart, 6));
      const requests = [api.get("/shifts/scheduled", { params: { from_date: from, to_date: to } })];
      if (isAdmin) {
        requests.push(api.get("/staff/list"), api.get("/clients"));
      }
      const results = await Promise.all(requests);
      setShifts(results[0].data || []);
      if (isAdmin) {
        setWorkers((results[1].data || []).filter((u) => ["peer_worker", "staff"].includes(u.role)));
        setClients(results[2].data || []);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [weekStart, isAdmin]);
  useEffect(() => { load(); }, [load]);

  const shiftsByWorkerDay = useMemo(() => {
    const map = {};
    (shifts || []).forEach((s) => {
      if (s.status === "cancelled") return;
      const iso = (s.scheduled_start || "").slice(0, 10);
      const key = `${s.user_id}|${iso}`;
      (map[key] = map[key] || []).push(s);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => (a.scheduled_start || "").localeCompare(b.scheduled_start || "")));
    return map;
  }, [shifts]);

  // For non-admins, derive the worker list from returned shifts (they only see their own).
  // Ensures the grid still renders a row even when the user has no shifts this week.
  const derivedWorkers = useMemo(() => {
    if (isAdmin) return workers;
    const seen = new Map();
    (shifts || []).forEach((s) => {
      if (!seen.has(s.user_id)) seen.set(s.user_id, { id: s.user_id, name: s.user_name, role: s.user_role });
    });
    if (user && !seen.has(user.id)) seen.set(user.id, { id: user.id, name: user.name, role: user.role });
    return Array.from(seen.values());
  }, [isAdmin, workers, shifts, user]);

  const visibleWorkers = useMemo(() => {
    if (!workerFilter) return derivedWorkers;
    return derivedWorkers.filter((w) => w.id === workerFilter);
  }, [derivedWorkers, workerFilter]);

  const canClockIntoShift = (s) => {
    if (!user || s.user_id !== user.id) return false;
    if (s.status !== "scheduled") return false;
    const now = new Date();
    const start = new Date(s.scheduled_start);
    const end = new Date(s.scheduled_end);
    const grace = 15 * 60 * 1000;
    return now.getTime() >= start.getTime() - grace && now.getTime() <= end.getTime();
  };

  const clockIntoShift = async (s) => {
    setBusyClockIn(s.id);
    try {
      await api.post("/me/shifts/clock-in", { shift_id: s.id });
      toast.success("Clocked in");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyClockIn(null); }
  };

  const weekLabel = `${weekStart.toLocaleDateString("en-AU", { day: "numeric", month: "short" })} — ${addDays(weekStart, 6).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}`;
  const jumpToday = () => setWeekStart(startOfWeek(new Date()));

  return (
    <div className="fade-in" data-testid="roster-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">Scheduling</div>
          <h2 className="font-serif text-3xl text-njas-navy">{isAdmin ? "Roster" : "My Roster"}</h2>
          <p className="text-sm text-njas-muted mt-1">
            {isAdmin
              ? "Plan peer worker and staff shifts. Workers clock into scheduled shifts from their calendar."
              : "Your upcoming shifts. Clock in from a shift within 15 minutes of its start time."}
          </p>
        </div>
        {isAdmin && (
          <button
            data-testid="add-shift-btn"
            onClick={() => setModal({ mode: "create", prefill: { scheduled_start: toDatetimeLocal(new Date().setHours(9, 0, 0, 0)) } })}
            className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-4 py-2 text-sm hover:bg-njas-navy-hover"
          >
            <Plus className="w-4 h-4" /> Schedule shift
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex items-center rounded-full bg-white border border-[var(--njas-border)] overflow-hidden">
          <button onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="roster-prev-week" className="p-2 hover:bg-njas-bg-alt"><ChevronLeft className="w-4 h-4" /></button>
          <div className="px-4 py-1.5 text-sm font-medium text-njas-navy">{weekLabel}</div>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="roster-next-week" className="p-2 hover:bg-njas-bg-alt"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <button onClick={jumpToday} className="text-xs rounded-full border border-[var(--njas-border)] px-3 py-1.5 text-njas-navy hover:bg-njas-bg-alt">This week</button>
        {isAdmin && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-njas-muted uppercase tracking-wider">Worker</span>
            <select value={workerFilter} onChange={(e) => setWorkerFilter(e.target.value)} className="rounded-lg border border-[var(--njas-border)] bg-white text-sm py-1.5 px-3" data-testid="roster-worker-filter">
              <option value="">All</option>
              {workers.map((w) => <option key={w.id} value={w.id}>{w.name} · {w.role.replace("_", " ")}</option>)}
            </select>
          </div>
        )}
      </div>

      {loading && <div className="text-sm text-njas-muted mb-3"><Loader2 className="inline w-3.5 h-3.5 animate-spin mr-1" /> Loading…</div>}

      <div className="rounded-xl bg-white border border-[var(--njas-border)] overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            {/* header row */}
            <div className="grid" style={{ gridTemplateColumns: "180px repeat(7, 1fr)" }}>
              <div className="bg-njas-bg-alt/60 border-b border-r border-[var(--njas-border)] px-3 py-2 text-[10px] uppercase tracking-wider text-njas-muted">Worker</div>
              {days.map((d, i) => {
                const isToday = isoDate(d) === isoDate(new Date());
                return (
                  <div key={i} className={`bg-njas-bg-alt/60 border-b border-r border-[var(--njas-border)] px-3 py-2 text-center ${isToday ? "text-njas-navy" : "text-njas-muted"}`}>
                    <div className="text-[10px] uppercase tracking-wider">{d.toLocaleDateString("en-AU", { weekday: "short" })}</div>
                    <div className={`text-sm font-semibold ${isToday ? "text-njas-sage-hover" : ""}`}>{d.getDate()}</div>
                  </div>
                );
              })}
            </div>

            {/* worker rows */}
            {visibleWorkers.length === 0 && (
              <div className="p-8 text-center text-sm text-njas-muted">No workers yet. Invite staff/peer workers from Staff Admin.</div>
            )}
            {visibleWorkers.map((w) => (
              <div key={w.id} className="grid" style={{ gridTemplateColumns: "180px repeat(7, 1fr)" }}>
                <div className="border-b border-r border-[var(--njas-border)] px-3 py-3 bg-white">
                  <div className="text-sm font-medium text-njas-navy">{w.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-njas-muted">{w.role.replace("_", " ")}</div>
                </div>
                {days.map((d, i) => {
                  const iso = isoDate(d);
                  const key = `${w.id}|${iso}`;
                  const cellShifts = shiftsByWorkerDay[key] || [];
                  const CellTag = isAdmin ? "button" : "div";
                  const cellProps = isAdmin ? {
                    onClick: () => setModal({
                      mode: "create",
                      prefill: {
                        user_id: w.id,
                        scheduled_start: `${iso}T09:00`,
                        scheduled_end: `${iso}T13:00`,
                      },
                    }),
                  } : {};
                  return (
                    <CellTag
                      key={i}
                      data-testid={`roster-cell-${w.id}-${iso}`}
                      {...cellProps}
                      className={`min-h-[76px] border-b border-r border-[var(--njas-border)] p-1.5 text-left align-top block w-full ${isAdmin ? "bg-white hover:bg-njas-bg-alt/50 transition-colors" : "bg-white"}`}
                    >
                      <div className="space-y-1">
                        {cellShifts.map((s) => {
                          const canClock = canClockIntoShift(s);
                          const chipClass = `text-[11px] px-2 py-1 rounded border cursor-pointer hover:opacity-80 ${
                            s.status === "active" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                            s.status === "completed" ? "bg-gray-100 text-gray-600 border-gray-200" :
                            "bg-njas-sage-tint text-njas-sage-hover border-[var(--njas-border)]"
                          }`;
                          return (
                            <div key={s.id} className={chipClass}
                              onClick={(e) => { e.stopPropagation(); setDetailShift(s); }}
                              data-testid={`roster-shift-${s.id}`}>
                              <div className="font-medium truncate">{fmtTime(s.scheduled_start)}–{fmtTime(s.scheduled_end)}</div>
                              <div className="truncate opacity-80">{s.client_name || "General"}</div>
                              {canClock && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); clockIntoShift(s); }}
                                  disabled={busyClockIn === s.id}
                                  data-testid={`roster-clock-in-${s.id}`}
                                  className="mt-1 w-full inline-flex items-center justify-center gap-1 rounded bg-emerald-600 text-white text-[10px] px-2 py-1 hover:bg-emerald-700 disabled:opacity-60"
                                >
                                  {busyClockIn === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Clock in
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CellTag>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 text-xs text-njas-muted mt-3">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-njas-sage-tint border border-[var(--njas-border)]" /> Scheduled</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-50 border border-emerald-200" /> In progress</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-100 border border-gray-200" /> Completed</span>
      </div>

      {modal && (
        <ScheduleShiftModal
          mode={modal.mode}
          prefill={modal.prefill}
          shift={modal.shift}
          workers={workers}
          clients={clients}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}

      {detailShift && (
        <ShiftDetailModal
          shift={detailShift}
          canEdit={isAdmin}
          onClose={() => setDetailShift(null)}
          onEdit={() => { setModal({ mode: "edit", shift: detailShift }); setDetailShift(null); }}
        />
      )}
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false });
}
